import { Container, Sprite, Texture } from 'pixi.js';
import { BoosterType } from '../types';
import { BoosterTutorialLayout } from '../../../../layouts/boosters/BoosterTutorialLayout';
import { applyLayoutState, revokeLayoutState } from '../../../libs/layouts/populateLayouts/applyLayoutState';
import { addClickReaction } from '../../utils/addClickReaction';
import { registerResizableObject } from '../../../utils/pixi/registerResizableObject';

type BoosterTutorialLayoutState = 'move-booster' | 'hammer-booster';

export class BoosterUseOverlayView extends Container {
  private readonly background = new Sprite(Texture.from('Fading_Corners')).props({
    parent: this,
    addAtIndex: 0,
    anchor: { x: 0.5, y: 0.5 },
    eventMode: 'none',
  });

  private readonly tutorialContainer = new Container().props({
    parent: this,
  });

  private readonly layout = new BoosterTutorialLayout();

  private layoutState: BoosterTutorialLayoutState | null = null;

  constructor() {
    super();

    this.interactive = false;
    this.visible = false;

    // Create tutorial hint layout.
    this.tutorialContainer.addChild(this.layout.root);

    registerResizableObject(this);
  }

  public setBoosterUseTutorial(boosterType: BoosterType, onCancelBooster: (() => void) | null): void {
    const { layout, layoutState: prevLayoutState } = this;
    const layoutState = `${BoosterType[boosterType].toString().toLowerCase()}-booster` as BoosterTutorialLayoutState;

    if (prevLayoutState) revokeLayoutState(layout, prevLayoutState);
    applyLayoutState(layout, layoutState);
    this.layoutState = layoutState;

    const { root, closeButton, wrapper } = layout;
    root.interactiveChildren = true;

    if (onCancelBooster === null) {
      closeButton.visible = false;
      wrapper.scale.set(1.3);
    } else {
      wrapper.scale.set(1);
      closeButton.visible = true;
      closeButton.onpointertap = (): void => {
        root.interactiveChildren = false;
        closeButton.onpointertap = undefined;
        onCancelBooster?.();
      };
    }

    addClickReaction({ target: closeButton });
  }

  public async show(): Promise<void> {
    const { background, layout } = this;

    this.visible = true;
    this.alpha = 0;
    background.alpha = 1;
    background.removeTweens();
    layout.root.removeTweens(true);

    layout.root.props({
      alpha: 0,
      scale: 0.65,
    });

    layout.boosterIcon.props({
      alpha: 0,
      scale: 0.1,
    });

    layout.boosterIconShadow.props({
      alpha: 0,
      scale: 0.1,
    });

    layout.textBubble.props({
      alpha: 0,
      scale: 0.6,
    });

    layout.root.tweenTo({ scaleX: 1, scaleY: 1, alpha: 1 }, 300, 'backOut', true, 250);
    layout.boosterIcon.tweenTo({ scaleX: 0.38, scaleY: 0.38, alpha: 1 }, 300, 'backOut', true, 350);
    layout.boosterIconShadow.tweenTo({ scaleX: 0.38, scaleY: 0.38, alpha: 1 }, 300, 'backOut', true, 350);
    layout.textBubble.tweenTo({ scaleX: 1, scaleY: 1, alpha: 1 }, 300, 'backOut', true, 350);

    await this.tweenTo({ alpha: 1 }, 300, 'quadOut', true).asyncOnComplete();
  }

  public async hide(): Promise<void> {
    const { background, layout } = this;

    layout?.root.tweenTo({ alpha: 0, scaleX: 0.5, scaleY: 0.5 }, 300, 'backIn', true);
    layout.boosterIcon.tweenTo({ scaleX: 0.1, scaleY: 0.1, alpha: 0 }, 300, 'backIn', true);
    layout.boosterIconShadow.tweenTo({ scaleX: 0.1, scaleY: 0.1, alpha: 0 }, 300, 'backIn', true);
    layout.textBubble.tweenTo({ scaleX: 0.5, scaleY: 0.5, alpha: 0 }, 300, 'backIn', true);

    await background.tweenTo({ alpha: 0 }, 300, 'quadIn', true).asyncOnComplete();

    this.visible = false;
  }

  public resize(width: number, height: number): void {
    const centerX = width / 2;
    const centerY = height / 2;
    this.background.props({ width, height, x: centerX, y: centerY });
    this.tutorialContainer.position.set(centerX, height - 200);
  }
}
